import React, { useEffect, useRef, useState } from 'react'
import { ProductTile } from '../tiles/ProductTile'
import { useTranslation } from 'next-i18next'
import { type SanityClient } from 'next-sanity'
import {
  getHref,
  getLocaleCode,
  replaceWildcards,
} from '../../lib/helper/helper'
import { SkeletonTile } from './SkeletonTile'
import { getClient } from '../../lib/sanity.client'
import { LocationTile } from '../tiles/LocationTile'
import { CategoryTile } from '../tiles/CategoryTile'
import { CollectionPageJsonLd } from 'next-seo'
import { urlForImage } from '../../lib/sanity.image'

interface Props {
  loadWith: (
    locale: AppLocale,
    client: SanityClient,
    offset?: number,
    packageSize?: number,
  ) => Promise<ReducedProduct[] | MajorLocation[]>
  showWidgetIfEmpty?: boolean
  widgetTopic?: string
  location?: string
  type?: 'product' | 'location' | 'category'
  documentType?: 'product' | 'location' | 'category'
  h1?: string
  wildcards?: Wildcards
  componentInBetween?: React.ReactNode
  addAsSchema?: boolean
}

const TileComponentMapping = {
  country: LocationTile,
  city: LocationTile,
  category: CategoryTile,
  region: LocationTile,
  state: LocationTile,
  product: ProductTile,
}

const CustomGrid = ({
  loadWith,
  type = 'product',
  documentType = 'product',
  h1,
  wildcards,
  componentInBetween,
  location,
  addAsSchema,
  showWidgetIfEmpty,
  widgetTopic,
}: Props) => {
  const { t, i18n } = useTranslation('common')
  const client = getClient()
  const packageSize = 100
  const [items, setItems] = useState<ReducedProduct[] | MajorLocation[]>([])
  const [loadingMore, setLoadingMore] = useState(true)
  const [endReached, setEndReached] = useState(false)
  const containerRef = useRef(null)
  const gridRef = useRef(null)
  const [topList, setTopList] = useState<ReducedProduct[] | MajorLocation[]>([])
  const [remainingListOne, setRemainingListOne] = useState<
    ReducedProduct[] | MajorLocation[]
  >([])
  const [remainingListTwo, setRemainingListTwo] = useState<
    ReducedProduct[] | MajorLocation[]
  >([])
  const [showMoreButton, setShowMoreButton] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      if (gridRef.current) {
        const computedStyle = getComputedStyle(gridRef.current)
        const columns = computedStyle
          .getPropertyValue('grid-template-columns')
          .split(' ').length

        let splitIndex
        switch (columns) {
          case 3:
            splitIndex =
              items.length >= 16
                ? 9
                : items.length <= 15 && items.length >= 6
                  ? 6
                  : items.length
            break
          case 4:
          case 2:
          case 1:
            splitIndex =
              items.length >= 16
                ? 8
                : items.length <= 15 && items.length >= 5
                  ? 4
                  : items.length
            break
        }

        setTopList(items.slice(0, splitIndex))
        setRemainingListOne(items.slice(splitIndex, splitIndex + 12))
        setRemainingListTwo(items.slice(splitIndex + 12))
      }
    }

    handleResize() // Initial calculation
    window.addEventListener('resize', handleResize)
    setShowMoreButton(items.length % 50 === 0 && items.length > 0)

    return () => window.removeEventListener('resize', handleResize)
  }, [items])

  const applyIndexOverwrite = (
    items: ReducedProduct[] | MajorLocation[],
  ): ReducedProduct[] | MajorLocation[] => {
    const itemsWithOverwrite = items.filter(
      (item) =>
        'index_overwrite' in item &&
        typeof item.index_overwrite === 'number' &&
        item.index_overwrite > 0,
    ) as ReducedProduct[]

    if (itemsWithOverwrite.length === 0) {
      return items
    }

    // Remove items with index_overwrite from their current positions
    const withoutOverwrite = items.filter(
      (item) =>
        !('index_overwrite' in item) ||
        typeof item.index_overwrite !== 'number' ||
        item.index_overwrite <= 0,
    )

    // Sort by index_overwrite value and insert at specified positions
    itemsWithOverwrite.sort(
      (a, b) => (a.index_overwrite || 0) - (b.index_overwrite || 0),
    )

    for (const item of itemsWithOverwrite) {
      const targetIndex = Math.min(
        (item.index_overwrite || 1) - 1,
        withoutOverwrite.length,
      )
      withoutOverwrite.splice(targetIndex, 0, item)
    }

    return withoutOverwrite as ReducedProduct[] | MajorLocation[]
  }

  useEffect(
    () => {
      loadWith(i18n.language as AppLocale, client, 0, packageSize).then(
        (fetchedItems) => {
          setItems(applyIndexOverwrite(fetchedItems))
          setLoadingMore(false)
        },
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language, loadWith],
  )

  const renderSkeletonTiles = () => {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-6 xs:mx-0 mx-3">
        {Array.from({ length: 12 }).map((_, index) => (
          <SkeletonTile key={index} type={type} isSlider={false} />
        ))}
      </div>
    )
  }

  const handleLoadMore = () => {
    setLoadingMore(true)
    loadWith(i18n.language as AppLocale, client, items.length, 25).then(
      (fetchedItems) => {
        if (fetchedItems.length === 0) {
          setEndReached(true)
        } else {
          const currentScrollTop = containerRef.current.scrollTop
          setItems(
            applyIndexOverwrite([...items, ...fetchedItems] as
              | ReducedProduct[]
              | MajorLocation[]),
          )
          containerRef.current.scrollTop = currentScrollTop
        }
        setLoadingMore(false)
      },
    )
  }

  const loadMore = (
    <div className="flex justify-center my-8">
      <button
        onClick={handleLoadMore}
        className={`btn btn-primary font-medium ${endReached ? '' : 'hover:text-primary'}`}
        disabled={loadingMore || endReached}
      >
        {loadingMore
          ? t('loading-more-' + type)
          : endReached
            ? t('end-reached-' + type)
            : t('load-more-' + type)}
      </button>
    </div>
  )

  const renderTile = (item: ReducedProduct | MajorLocation, index: number) => {
    const TileComponent =
      TileComponentMapping[item._type as keyof typeof TileComponentMapping] ||
      null
    if (TileComponent) {
      return (
        <div className="embla__slide" key={index}>
          <TileComponent
            key={index}
            item={item as never}
            type={type as never}
          />
        </div>
      )
    }
    return null
  }
  if (items.length === 0 && !loadingMore) {
    if (showWidgetIfEmpty) {
      const getAmountToShow = () => {
        if (typeof window === 'undefined') return 9 // Default for SSR
        const width = window.innerWidth
        if (width >= 815) return 8
        if (width >= 595) return 9
        if (width >= 585) return 8
        return 9
      }

      const amountToShow = getAmountToShow()
      return (
        <div className="relative flex flex-col items-stretch w-full">
          {type !== 'location' && (
            <h2 className="text-xl text-basis font-medium ml-4 xs:ml-0 mt-12 mb-4 text-left">
              {location
                ? t('top-products-in-location', {
                    amount: amountToShow,
                    location,
                  })
                : t('top-products', { amount: topList.length })}
            </h2>
          )}
          <div
            className="xs:mx-0 mx-3"
            key={`gyg-widget-${widgetTopic}`}
            data-gyg-href="https://widget.getyourguide.com/default/activities.frame"
            data-gyg-locale-code={getLocaleCode(i18n.language as AppLocale)}
            data-gyg-widget="activities"
            data-gyg-number-of-items={amountToShow}
            data-gyg-partner-id="W60CGI8"
            data-gyg-q={widgetTopic || location}
            data-gyg-cmp="s2s-pw"
          ></div>
          <div
            className="absolute bottom-0 left-0 right-0 w-full bg-white z-40"
            style={{ height: '48px' }}
          ></div>
        </div>
      )
    } else {
      return null
    }
  }

  return (
    <>
      {addAsSchema && (
        <CollectionPageJsonLd
          name={replaceWildcards(h1)}
          hasPart={items.map((item: ReducedProduct | MajorLocation) => {
            const description =
              'description_1' in item
                ? item.description_1
                : 'nameDescription' in item
                  ? item.nameDescription
                  : ''
            const image =
              'coverImage' in item
                ? item.coverImage?.image
                : 'image' in item
                  ? item.image?.image
                  : ''
            if (!item.h1) {
              return null
            }
            return {
              name:
                'location' in item
                  ? item.h1.replace('[city]', item.location?.name || '')
                  : item.h1,
              about: description,
              url: `https://sight2sight.com${i18n.language === 'en' ? '' : '/' + i18n.language}${getHref(item, i18n.language as AppLocale)}`,
              thumbnailUrl: image ? urlForImage(image)?.url() : '',
            }
          })}
        />
      )}
      <div className="max-w-screen-xl mx-auto" ref={containerRef}>
        {h1 &&
          (documentType === 'location' ? null : (
            <b>
              {documentType === 'category' ? (
                <h2 className="uppercase text-2xl xs:text-3xl my-3 mx-3 xs:mx-0 xs:mt-12 mt-6 mb-4">
                  {replaceWildcards(h1, wildcards)}
                </h2>
              ) : (
                <h1 className="uppercase text-2xl xs:text-3xl my-3 mx-3 xs:mx-0 xs:mt-12 mt-6 mb-4">
                  {replaceWildcards(h1, wildcards)}
                </h1>
              )}
            </b>
          ))}
        {loadingMore && items.length === 0 ? (
          renderSkeletonTiles()
        ) : (
          <>
            {!h1 && type !== 'location' && (
              <h2 className="text-xl text-basis font-medium ml-4 xs:ml-0 mt-12 mb-4 text-left">
                {location
                  ? t('top-products-in-location', {
                      amount: !!topList.length ? topList.length : '',
                      location,
                    })
                  : t('top-products', { amount: topList.length })}
              </h2>
            )}
            <div
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-6 xs:mx-0 mx-3"
              ref={gridRef}
            >
              {topList.length > 0 &&
                [...topList, ...(h1 ? remainingListOne : [])].map(renderTile)}
            </div>
            {!h1 && remainingListOne.length > 0 && (
              <>
                {type !== 'location' ? (
                  <h2 className="text-xl text-basis font-medium ml-4 xs:ml-0 mt-12 mb-4 text-left">
                    {location
                      ? t('more-products-in-location', {
                          location,
                        })
                      : t('more-products')}
                  </h2>
                ) : (
                  <div className="h-6" />
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-6 xs:mx-0 mx-3">
                  {remainingListOne.map(renderTile)}
                </div>
              </>
            )}
            {!remainingListTwo.length && showMoreButton && loadMore}
            {componentInBetween || <div className="h-4" />}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-6 xs:mx-0 mx-3">
              {remainingListTwo.map(renderTile)}
            </div>
            {!!remainingListTwo.length && showMoreButton && loadMore}
          </>
        )}
      </div>
    </>
  )
}
export default CustomGrid
